"use client";

import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { REMOVAL_UNDO_MS } from "@/features/cart/hooks/use-removal-queue";
import { cn } from "@/lib/utils";

const RING_RADIUS = 15;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const TOTAL_SECONDS = Math.round(REMOVAL_UNDO_MS / 1000);

const CountdownRing = () => {
  const t = useTranslations();
  const [secondsLeft, setSecondsLeft] = useState(TOTAL_SECONDS);

  useEffect(() => {
    const interval = setInterval(
      () => setSecondsLeft((seconds) => Math.max(0, seconds - 1)),
      1000,
    );

    return () => clearInterval(interval);
  }, []);

  return (
    <span className="relative size-8 shrink-0">
      <svg aria-hidden="true" className="size-8 -rotate-90" viewBox="0 0 32 32">
        <circle
          className="stroke-brand-grey2"
          cx="16"
          cy="16"
          fill="none"
          r={RING_RADIUS}
          strokeWidth="2"
        />
        <circle
          className="stroke-brand-green1-300 transition-[stroke-dashoffset] duration-1000 ease-linear"
          cx="16"
          cy="16"
          fill="none"
          r={RING_RADIUS}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={
            RING_CIRCUMFERENCE * (1 - secondsLeft / TOTAL_SECONDS)
          }
          strokeLinecap="round"
          strokeWidth="2"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-medium text-brand-green1-300 text-sm">
        {t("cart.undo.countdown", { seconds: secondsLeft })}
      </span>
    </span>
  );
};

export const RemovedItemNotice = ({
  className,
  label,
  onUndo,
}: {
  className?: string;
  label: string;
  // Dropped once the write is on its way: the position cannot come back, and a
  // button that no longer undoes anything must not stay on screen.
  onUndo?: () => void;
}) => {
  const t = useTranslations();

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-1",
        className,
      )}
      data-pw="removed-item-notice"
    >
      <p
        aria-live="polite"
        className="text-center font-medium text-brand-green1-300 text-xl leading-7"
      >
        {label}
      </p>
      {onUndo ? (
        <div className="flex items-center gap-2">
          <CountdownRing />
          <button
            className="flex h-12 items-center font-medium text-base text-brand-green1-300 underline underline-offset-4"
            data-pw="undo-removal-button"
            onClick={onUndo}
            type="button"
          >
            {t("cart.undo.action")}
          </button>
        </div>
      ) : (
        // Keeps the block the same height while the write is in flight, so the
        // notice does not jump right before it disappears.
        <div className="h-12" />
      )}
    </div>
  );
};
